import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { clientsApi } from "@/lib/api";

const useCountdown = (targetIso) => {
  const [remaining, setRemaining] = useState(() =>
    targetIso ? new Date(targetIso).getTime() - Date.now() : 0
  );
  const timer = useRef(null);

  useEffect(() => {
    if (!targetIso) { setRemaining(0); return; }
    const tick = () => setRemaining(new Date(targetIso).getTime() - Date.now());
    tick();
    timer.current = setInterval(tick, 1000);
    return () => clearInterval(timer.current);
  }, [targetIso]);

  return remaining;
};

const Countdown = ({ ms }) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const units = [
    { label: "Days", value: days },
    { label: "Hours", value: hours },
    { label: "Minutes", value: mins },
    { label: "Seconds", value: secs },
  ];
  return (
    <div className="tss-countdown" data-testid="countdown-timer">
      <p className="tss-countdown-label">Live Event Starts In</p>
      <div className="tss-countdown-grid">
        {units.map((u) => (
          <div className="tss-countdown-unit" key={u.label}>
            <span className="tss-countdown-value">{String(u.value).padStart(2, "0")}</span>
            <span className="tss-countdown-unit-label">{u.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const hexToRgb = (hex) => {
  if (!hex) return null;
  const m = hex.replace("#", "").match(/^([0-9a-f]{6})$/i);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

const buildBrandStyle = (c) => {
  if (!c) return undefined;
  const style = {};
  const rgb = hexToRgb(c.primary_color);
  if (rgb) style["--client-primary-rgb"] = rgb;
  if (c.secondary_color) style["--client-secondary"] = c.secondary_color;
  if (c.accent_color) style["--client-accent"] = c.accent_color;
  if (c.background_image_url) style["--client-bg-image"] = `url("${c.background_image_url}")`;
  style["--client-accent-font"] =
    c.font_family === "sans" ? "'Poppins', 'Helvetica Neue', sans-serif" : "'Lora', Georgia, serif";
  return style;
};

const INACTIVE_REDIRECT_URL = "https://thestreamstop.com";

const ClientPage = () => {
  const { slug } = useParams();
  const [client, setClient] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let active = true;
    setStatus("loading");
    clientsApi
      .getBySlug(slug)
      .then((data) => {
        if (!active) return;
        // Inactive site: redirect before any show UI mounts (no flash).
        if (data.is_active === false) {
          setStatus("redirecting");
          window.location.replace(INACTIVE_REDIRECT_URL);
          return;
        }
        setClient(data);
        setStatus("ready");
        if (data.displayTitle) document.title = data.displayTitle;
      })
      .catch(() => active && setStatus("notfound"));
    return () => { active = false; };
  }, [slug]);

  // Lightweight polling so title / embed / status note stay current without a refresh
  useEffect(() => {
    if (status !== "ready") return;
    const id = setInterval(() => {
      clientsApi
        .getBySlug(slug)
        .then((data) => {
          if (data.is_active === false) {
            window.location.replace(INACTIVE_REDIRECT_URL);
            return;
          }
          setClient((prev) => {
            if (prev && JSON.stringify(prev) === JSON.stringify(data)) return prev;
            if (data.displayTitle) document.title = data.displayTitle;
            return data;
          });
        })
        .catch(() => {});
    }, 45000);
    return () => clearInterval(id);
  }, [slug, status]);

  const remaining = useCountdown(status === "ready" ? client?.starts_at : null);
  const isPreLive = !!client?.starts_at && remaining > 0;

  if (status === "loading" || status === "redirecting") {
    return (
      <div className="tss-hero" data-testid="client-loading">
        <div className="tss-hero-inner">
          <p className="tss-powered" style={{ marginTop: 0 }}>Loading…</p>
        </div>
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="tss-hero" data-testid="client-notfound">
        <div className="tss-hero-inner">
          <h1 className="tss-title">
            Stream Not Found
            <span className="accent">Check Your Link</span>
          </h1>
          <p className="tss-powered">Powered by The Stream Stop</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tss-hero tss-hero--ready" data-testid="client-page" style={buildBrandStyle(client)}>
      <div className="tss-hero-inner">
        {client.logoUrl && (
          <img className="tss-hero-logo" src={client.logoUrl} alt={client.name} data-testid="client-logo" />
        )}
        {client.displayTitle && (
          <div className="tss-titlerow">
            {client.title_logo_url && (
              <img className="tss-title-logo" src={client.title_logo_url} alt="" data-testid="client-title-logo" />
            )}
            <h1 className="tss-title" data-testid="client-title">{client.displayTitle}</h1>
          </div>
        )}
        {isPreLive ? (
          <Countdown ms={remaining} />
        ) : (
          <div
            className="tss-player-wrap"
            data-testid="client-embed"
            dangerouslySetInnerHTML={{ __html: client.embedCode }}
          />
        )}
        {client.status_note && (
          <p className="tss-status-note" data-testid="client-status-note">{client.status_note}</p>
        )}
        <p className="tss-powered">Powered by The Stream Stop</p>
      </div>
    </div>
  );
};

export default ClientPage;
